import { driver } from "driver.js";
import "driver.js/dist/driver.css";

const STORAGE_KEY = "blog.walkthrough.v1";

const readTour = () => {
    try {
        return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}") || {};
    } catch {
        return {};
    }
};

const writeTour = (patch) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...readTour(), ...patch }));
};

export const hasSeenBlogTour = (page) => Boolean(readTour()[page]);

export const isBlogTourResume = (page) => readTour().resume === page;

export const resetBlogTour = () => {
    localStorage.removeItem(STORAGE_KEY);
};

const withExisting = (steps) => steps.filter((step) => (
    !step.element || document.querySelector(step.element)
));

const baseConfig = {
    showProgress: true,
    animate: true,
    allowClose: true,
    overlayColor: "rgba(17, 17, 26, 0.42)",
    stagePadding: 8,
    stageRadius: 8,
    popoverClass: "blog-tour",
    nextBtnText: "Next",
    prevBtnText: "Back",
    doneBtnText: "Done",
    progressText: "{{current}} of {{total}}",
};

let activeTour = null;

const start = (config) => {
    activeTour?.destroy();
    activeTour = driver(config);
    activeTour.drive();
    return activeTour;
};

export const startFeedTour = ({ onWrite } = {}) => {
    let handingOff = false;
    const tour = start({
        ...baseConfig,
        steps: withExisting([
            {
                element: "[data-blog-tour='welcome']",
                popover: {
                    title: "Team blog",
                    description: "Notes for whoever comes next — how a thing works, a gotcha, or a question you still have.",
                    side: "bottom",
                    align: "start",
                },
            },
            {
                element: "[data-blog-tour='search']",
                popover: {
                    title: "Search by meaning",
                    description: "Ask in plain language. Try “employer cash call” or “in-network” — it looks through the posts, not just titles.",
                    side: "bottom",
                    align: "start",
                },
            },
            {
                element: "[data-blog-tour='from']",
                popover: {
                    title: "Narrow the list",
                    description: "Open filters to pick a date range or an author. Click a name on a card to do the same.",
                    side: "bottom",
                    align: "end",
                },
            },
            {
                element: "[data-blog-tour='write']",
                popover: {
                    title: "Write a post",
                    description: "Start here when you have something to leave. Next opens the Writer assistant so you can talk it through.",
                    side: "bottom",
                    align: "end",
                    nextBtnText: "Write a post",
                    onNextClick: () => {
                        handingOff = true;
                        writeTour({ feed: true, resume: "compose" });
                        tour.destroy();
                        onWrite?.();
                    },
                },
            },
        ]),
        onDestroyed: () => {
            if (activeTour === tour) activeTour = null;
            if (handingOff) return;
            writeTour({ feed: true });
        },
    });
};

export const startComposeTour = () => {
    const tour = start({
        ...baseConfig,
        steps: withExisting([
            {
                element: "[data-blog-tour='title']",
                popover: {
                    title: "Title",
                    description: "One line for what this is about. Keep it operational so people can find it later.",
                    side: "bottom",
                    align: "start",
                },
            },
            {
                element: "[data-blog-tour='media']",
                popover: {
                    title: "Media",
                    description: "Optional short clip. Drop a video here, or record up to one minute.",
                    side: "bottom",
                    align: "start",
                },
            },
            {
                element: "[data-blog-tour='body']",
                popover: {
                    title: "Body",
                    description: "Write the note. Headings, lists, tables, images, and links. We check for PHI, PII, and foul language before it publishes.",
                    side: "top",
                    align: "start",
                },
            },
            {
                element: "[data-blog-tour='writer']",
                popover: {
                    title: "Writer assistant",
                    description: "Talk or type. It interviews you and fills the form for you to review. Alt+M (⌥M on a Mac) toggles the mic.",
                    side: "left",
                    align: "start",
                },
            },
        ]),
        onDestroyed: () => {
            if (activeTour === tour) activeTour = null;
            writeTour({ compose: true, resume: "" });
        },
    });
};
